import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { RichTextEditor } from './RichTextEditor';
import { InternalLinksPanel } from './InternalLinksPanel';
import { ArrowLeft, Save, X, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const aiAutofill = async (field, context) => {
  const res = await fetch(`${BACKEND_URL}/api/admin/ai-blog/autofill`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ field, context })
  });
  if (!res.ok) throw new Error('AI generation failed');
  return res.json();
};

const AiButton = ({ onClick, loading, label = 'AI' }) => (
  <Button type="button" variant="ghost" size="sm" onClick={onClick} disabled={loading}
    className="h-7 text-xs text-purple-600 hover:bg-purple-50 hover:text-purple-700 gap-1" data-testid={`ai-fill-${label.toLowerCase()}`}>
    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
    {label}
  </Button>
);

export const BlogEditor = () => {
  const navigate = useNavigate();
  const { blogId } = useParams();
  const isEditMode = blogId && blogId !== 'new';

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'finance',
    tags: [],
    featuredImage: '',
    author: 'NSRM & Associates',
    metaTitle: '',
    metaDescription: '',
    status: 'draft'
  });

  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState({});

  const handleAiFill = async (field) => {
    const context = formData.title || formData.excerpt || 'virtual CFO services India';
    setAiLoading(prev => ({ ...prev, [field]: true }));
    try {
      const result = await aiAutofill(field, context);
      if (field === 'title' && result.suggestions) {
        setFormData(prev => ({ ...prev, title: result.suggestions[0], slug: generateSlug(result.suggestions[0]), metaTitle: result.suggestions[0] }));
        toast({ title: 'AI Titles', description: result.suggestions.join(' | ') });
      } else if (field === 'excerpt' && result.excerpt) {
        setFormData(prev => ({ ...prev, excerpt: result.excerpt }));
      } else if (field === 'tags' && result.tags) {
        setFormData(prev => ({ ...prev, tags: [...new Set([...prev.tags, ...result.tags])] }));
      } else if (field === 'seo_title' && result.seo_title) {
        setFormData(prev => ({ ...prev, metaTitle: result.seo_title }));
      } else if (field === 'seo_description' && result.seo_description) {
        setFormData(prev => ({ ...prev, metaDescription: result.seo_description }));
      } else if (field === 'slug' && result.slug) {
        setFormData(prev => ({ ...prev, slug: result.slug }));
      }
    } catch (e) {
      toast({ title: 'AI Error', description: e.message, variant: 'destructive' });
    } finally {
      setAiLoading(prev => ({ ...prev, [field]: false }));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isEditMode) {
      fetchBlog();
    }
  }, [blogId, isEditMode]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blogs/${blogId}`);
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          content: data.content,
          category: data.category,
          tags: data.tags,
          featuredImage: data.featuredImage || '',
          author: data.author,
          metaTitle: data.metaTitle || '',
          metaDescription: data.metaDescription || '',
          status: data.status
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load blog",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\\s-]/g, '')
      .replace(/\\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (value) => {
    setFormData({
      ...formData,
      title: value,
      slug: generateSlug(value),
      metaTitle: value
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleSave = async (publishNow = false) => {
    setSaving(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const dataToSave = {
        ...formData,
        status: publishNow ? 'published' : formData.status
      };

      let response;
      if (isEditMode) {
        response = await fetch(`${BACKEND_URL}/api/blogs/${blogId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSave)
        });
      } else {
        response = await fetch(`${BACKEND_URL}/api/blogs/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSave)
        });
      }

      if (response.ok) {
        toast({
          title: "Success!",
          description: publishNow ? "Blog published successfully" : "Blog saved as draft",
        });
        navigate('/admin/dashboard');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save blog",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <h1 className="text-xl font-bold">Loading Blog...</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card><CardContent className="pt-6 space-y-4">
                <div className="h-5 w-20 bg-gray-200 rounded animate-pulse" />
                <div className="h-10 bg-gray-200 rounded animate-pulse" />
                <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
                <div className="h-10 bg-gray-200 rounded animate-pulse" />
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <div className="h-5 w-16 bg-gray-200 rounded animate-pulse mb-4" />
                <div className="h-64 bg-gray-100 rounded animate-pulse" />
              </CardContent></Card>
            </div>
            <div className="space-y-6">
              <Card><CardContent className="pt-6 space-y-3">
                <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="h-10 bg-gray-200 rounded animate-pulse" />
                <div className="h-10 bg-gray-200 rounded animate-pulse" />
              </CardContent></Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="text-xl font-bold">
                {isEditMode ? 'Edit Blog' : 'Create New Blog'}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </Button>
              <Button onClick={() => handleSave(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? 'Publishing...' : 'Publish'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Blog Title</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Title *</Label>
                    <AiButton onClick={() => handleAiFill('title')} loading={aiLoading.title} label="AI Suggest" />
                  </div>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Enter blog title"
                    className="text-xl font-semibold"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Slug (URL)</Label>
                    <AiButton onClick={() => handleAiFill('slug')} loading={aiLoading.slug} label="AI Slug" />
                  </div>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="blog-url-slug"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Excerpt *</Label>
                    <AiButton onClick={() => handleAiFill('excerpt')} loading={aiLoading.excerpt} label="AI Excerpt" />
                  </div>
                  <Textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="Short description"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Content *</CardTitle>
              </CardHeader>
              <CardContent>
                <RichTextEditor
                  content={formData.content}
                  onChange={(html) => setFormData({ ...formData, content: html })}
                />
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="tax">Tax</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                      <SelectItem value="fdi">FDI & India Entry</SelectItem>
                      <SelectItem value="quick_read">Quick Read</SelectItem>
                      <SelectItem value="payroll">Payroll</SelectItem>
                      <SelectItem value="business_tips">Business Tips</SelectItem>
                      <SelectItem value="case_study">Case Study</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Author</Label>
                  <Input
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Tags</CardTitle>
                  <AiButton onClick={() => handleAiFill('tags')} loading={aiLoading.tags} label="AI Tags" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add tag"
                  />
                  <Button type="button" onClick={handleAddTag}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, idx) => (
                    <Badge key={tag || `tag-${idx}`} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => handleRemoveTag(tag)} />
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Featured Image</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  value={formData.featuredImage}
                  onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
                {formData.featuredImage && (
                  <img src={formData.featuredImage} alt="Featured" className="w-full h-40 object-cover rounded-lg" />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>SEO Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Meta Title</Label>
                    <AiButton onClick={() => handleAiFill('seo_title')} loading={aiLoading.seo_title} label="AI SEO" />
                  </div>
                  <Input
                    value={formData.metaTitle}
                    onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                    placeholder="SEO title"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Meta Description</Label>
                    <AiButton onClick={() => handleAiFill('seo_description')} loading={aiLoading.seo_description} label="AI Meta" />
                  </div>
                  <Textarea
                    value={formData.metaDescription}
                    onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                    placeholder="SEO description"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Internal Links Panel */}
            <InternalLinksPanel
              blogId={isEditMode ? blogId : null}
              blogData={formData}
            />
          </div>
        </div>
      </div>
      <Toaster />
    </div>
  );
};
